import PropTypes from 'prop-types';
import { Users, ChevronRight } from 'lucide-react';
import { Heading, Text, Stack } from '@/components/ui/index.js';
import styles from './GroupCard.module.css';

const sportIcons = {
  basketball: '🏀',
  soccer: '⚽',
  tennis: '🎾',
  running: '🏃',
  swimming: '🏊',
  cycling: '🚴',
  yoga: '🧘',
};

/**
 * GroupCard - Displays group information with member count and unread indicator
 * 
 * @param {Object} group - Group data object
 * @param {function} onClick - Click handler
 * @param {boolean} isDiscover - If true, shows "Discover" style card
 * @param {Object} style - Additional inline styles
 */
export function GroupCard({ group, onClick, isDiscover = false, style }) {
  if (isDiscover) {
    return (
      <article 
        className={`${styles.card} ${styles.discover}`}
        onClick={onClick}
        style={style}
      >
        <div className={styles.discoverContent}>
          <div className={styles.discoverIcon}>
            <ChevronRight size={24} />
          </div>
          <Heading level={3} size="sm" color="primary" align="center">
            Discover Groups
          </Heading>
          <Text size="xs" color="muted" align="center">
            Find your community
          </Text>
        </div>
      </article>
    );
  }

  return (
    <article 
      className={styles.card}
      onClick={onClick}
      style={style}
    >
      {/* Group Image/Icon */}
      <div className={styles.imageContainer}>
        <div className={styles.imagePlaceholder}>
          <span className={styles.sportEmoji}>
            {sportIcons[group.sport] || '🏃'}
          </span>
        </div>
        {group.unreadCount > 0 && (
          <span className={styles.unreadBadge}>
            {group.unreadCount > 99 ? '99+' : group.unreadCount}
          </span>
        )}
      </div>

      {/* Group Info */}
      <div className={styles.content}>
        <Stack direction="column" gap={1}>
          <Heading level={3} size="sm" color="primary" className={styles.name}>
            {group.name}
          </Heading>
          <div className={styles.memberCount}>
            <Users size={12} strokeWidth={2} />
            <Text size="xs" color="muted">
              {group.memberCount.toLocaleString()} members
            </Text>
          </div>
        </Stack>
      </div>
    </article>
  );
}

GroupCard.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    memberCount: PropTypes.number.isRequired,
    unreadCount: PropTypes.number.isRequired,
    image: PropTypes.string,
    sport: PropTypes.string,
  }),
  onClick: PropTypes.func,
  isDiscover: PropTypes.bool,
  style: PropTypes.object,
};

export default GroupCard;
